import { useEffect } from 'react';
import { useMemberDetail } from '../../hooks/useMemberDetail';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { ErrorMessage } from '../../components/shared/ErrorMessage';

interface MemberInfoPopupProps {
  userId: string | null;
  onClose: () => void;
}

export function MemberInfoPopup({ userId, onClose }: MemberInfoPopupProps) {
  const { data, isLoading, isError, error } = useMemberDetail(userId);

  // Close on Escape key
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!userId) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9000,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="member-popup-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '0',
          maxWidth: '520px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px 16px',
            borderBottom: '1px solid #e0e0e0',
            position: 'sticky',
            top: 0,
            backgroundColor: '#fff',
            zIndex: 1,
          }}
        >
          <h2 id="member-popup-title" style={{ margin: 0, fontSize: '1.2rem', color: '#1a1a1a' }}>
            Member Details
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.4rem',
              color: '#555',
              lineHeight: 1,
              padding: '4px 8px',
              borderRadius: '4px',
            }}
          >
            &#10005;
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px' }}>
          {isLoading && <LoadingSpinner message="Loading member details..." />}
          {isError && (
            <ErrorMessage message={error?.message ?? 'Failed to load member details.'} />
          )}
          {data && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <InfoField label="First Name" value={data.firstName} />
                <InfoField label="Last Name" value={data.lastName} />
                <InfoField label="Company" value={data.companyName} />
                <InfoField label="Email" value={data.email} />
                {data.department && (
                  <InfoField label="Department" value={data.department} />
                )}
                {data.jobTitle && (
                  <InfoField label="Job Title" value={data.jobTitle} />
                )}
              </div>

              {data.brgMemberships.length > 0 && (
                <div>
                  <h3 style={{ margin: '0 0 12px', fontSize: '1rem', color: '#444' }}>
                    BRG Memberships
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {data.brgMemberships.map((m) => (
                      <div
                        key={m.brgId}
                        style={{
                          padding: '10px 14px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '6px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{m.brgName}</span>
                        <span
                          style={{
                            fontSize: '0.8rem',
                            color: '#555',
                            backgroundColor: '#e0e0e0',
                            borderRadius: '12px',
                            padding: '2px 10px',
                          }}
                        >
                          {m.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.brgMemberships.length === 0 && (
                <p style={{ color: '#888', fontSize: '0.9rem' }}>No BRG memberships.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoField({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
        {label}
      </div>
      <div style={{ fontSize: '0.95rem', color: '#1a1a1a' }}>{value || '—'}</div>
    </div>
  );
}
